/**
 * PhotoAttachment — public shell + orchestrator.
 *
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.1 for the full contract.
 *
 * Controlled component: parent owns the `photos` array on the domain record.
 * This shell:
 *   1. Runs new files through `usePhotoCapture.processFile(s)` (pipeline
 *      handles HEIC/EXIF/GPS/resize/thumb; savePhoto writes to IDB + enqueues
 *      to outbox per FT-341/342/344).
 *   2. Bubbles the resulting PhotoRef to the parent via `onChange` ONLY AFTER
 *      the IDB write succeeds — never before (parent-UI-shows-record-lost-photo).
 *   3. Owns viewer + delete-confirm modal state.
 *   4. Enforces `maxCount` at capture time (belt+braces with PhotoCaptureButton).
 *
 * Consumers (FT-345 / FT-346 / FT-347) will drop this into Equipment,
 * Maintenance, and Defects screens. The shell must not care which record
 * type it lives inside beyond passing `recordType` / `recordId` through.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { devWarn } from '../../../utils/devLog';
import { usePhotoStore } from './hooks/usePhotoStore';
import { usePhotoCapture } from './hooks/usePhotoCapture';
import { PhotoCaptureButton } from './PhotoCaptureButton';
import { PhotoThumbnailGrid } from './PhotoThumbnailGrid';
import { PhotoViewer } from './PhotoViewer';
import { PhotoDeleteConfirm } from './PhotoDeleteConfirm';
import { PhotoProcessingOverlay } from './PhotoProcessingOverlay';
import { isTombstoned } from '../../../sync/tombstone';

const DEFAULT_MAX = 20;

/**
 * FT-565: mark a PhotoRef deleted IN PLACE with the FT-458 tombstone
 * convention (canonical snake `deleted_at`, ISO string — see sync/tombstone.js)
 * instead of splicing it out of the array.
 */
const tombstonePhotoRef = (ref, deletedBy) => ({
  ...ref,
  deleted_at: new Date().toISOString(),
  ...(deletedBy ? { deleted_by: deletedBy } : {}),
});

/**
 * FT-565: the tombstoned ref(s) a delete / replace should keep in the array.
 * Empty when tombstone mode is off, or the target can't carry a marker (only
 * PhotoRefs with a photoId can — legacy base64 strings are spliced as before).
 */
const tombstonesFor = (target, enabled, deletedBy) =>
  (enabled && target && typeof target === 'object' && target.photoId
    ? [tombstonePhotoRef(target, deletedBy)]
    : []);

/**
 * @param {Object} props
 * @param {'equipment_document'|'maintenance_job'|'defect'} props.recordType
 * @param {string} props.recordId
 * @param {string} props.vesselId
 * @param {(import('./types').PhotoRef | string)[]} [props.photos]
 * @param {(next: (import('./types').PhotoRef | string)[]) => void} props.onChange
 * @param {number} [props.maxCount]
 * @param {'standard'|'high'} [props.captureQuality]
 * @param {boolean} [props.caption]
 * @param {string} [props.capturedBy]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.emptyLabel]
 * @param {string} [props.testId]
 * @param {boolean} [props.tombstoneOnDelete] FT-565 (opt-in, default false).
 *   When true, Delete / Replace mark the PhotoRef with a `deleted_at`
 *   tombstone and KEEP it in the emitted array (so sync merges can tell
 *   "deliberately deleted" from "never seen" and never resurrect it), and
 *   tombstoned refs are hidden from the grid / viewer / count. Required for
 *   any record whose `photos` field is list-merged by sync
 *   (sync/mergeListFields.js LIST_FIELD_MERGE). Legacy base64 string entries
 *   cannot carry a marker and are still spliced out. When false (default),
 *   behaviour is exactly as before (hard removal).
 */
export function PhotoAttachment({
  recordType,
  recordId,
  vesselId,
  photos = [],
  onChange,
  maxCount = DEFAULT_MAX,
  captureQuality = 'standard',
  caption = false,
  capturedBy,
  disabled = false,
  className = '',
  emptyLabel = 'No photos yet.',
  testId = 'photo-attachment',
  tombstoneOnDelete = false,
}) {
  const store = usePhotoStore(vesselId);
  const capture = usePhotoCapture(vesselId);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  // FT-349d: ref exposed by PhotoCaptureButton (forwardRef + useImperativeHandle)
  // so Replace flow can programmatically re-open the camera picker.
  const captureButtonRef = useRef(null);

  const allPhotos = useMemo(
    () => (Array.isArray(photos) ? photos : []),
    [photos],
  );

  // FT-565: `safePhotos` is the VISIBLE list — every index-based operation
  // (grid, viewer, delete, caption, cap) works on it. In tombstone mode the
  // tombstoned refs are held aside and re-attached on every emit so they are
  // never dropped from the persisted record. With tombstoneOnDelete=false
  // nothing is filtered (identical to pre-FT-565 behaviour).
  const safePhotos = useMemo(
    () => (tombstoneOnDelete ? allPhotos.filter((p) => !isTombstoned(p)) : allPhotos),
    [allPhotos, tombstoneOnDelete],
  );
  const hiddenTombstones = useMemo(
    () => (tombstoneOnDelete ? allPhotos.filter((p) => isTombstoned(p)) : []),
    [allPhotos, tombstoneOnDelete],
  );

  /**
   * @param {Array} nextVisible  the next VISIBLE list
   * @param {Array} [newTombstones] refs deleted by this action (tombstone mode only)
   */
  const emitChange = useCallback(
    (nextVisible, newTombstones = []) => {
      if (typeof onChange !== 'function') return;
      onChange(
        tombstoneOnDelete
          ? [...nextVisible, ...hiddenTombstones, ...newTombstones]
          : nextVisible,
      );
    },
    [onChange, tombstoneOnDelete, hiddenTombstones],
  );

  // ── Capture ───────────────────────────────────────────────────────────
  const handleFilesPicked = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      if (disabled) return;
      if (!vesselId) {
        toast.error('Cannot save photo without a selected vessel.');
        return;
      }
      if (!recordId) {
        toast.error('Cannot save photo without a record id.');
        return;
      }
      // Belt+braces cap enforcement — even if PhotoCaptureButton lets it through.
      const remaining = Math.max(0, maxCount - safePhotos.length);
      if (remaining === 0) {
        toast.error(`Max ${maxCount} photos reached.`);
        return;
      }
      const batch = files.slice(0, remaining);
      const options = {
        recordType,
        recordId,
        capturedBy: capturedBy || 'unknown',
        quality: captureQuality,
      };
      try {
        if (batch.length === 1) {
          const ref = await capture.processFile(batch[0], options);
          // onChange runs AFTER IDB write — see failure-mode notes.
          emitChange([...safePhotos, ref]);
        } else {
          const refs = await capture.processFiles(batch, options);
          if (refs.length > 0) emitChange([...safePhotos, ...refs]);
        }
      } catch (err) {
        // FT-577: a batch that partly succeeded still attaches the photos that
        // WERE saved (they're already in IDB + queued) before reporting.
        if (Array.isArray(err?.refs) && err.refs.length > 0) {
          emitChange([...safePhotos, ...err.refs]);
        }
        // Plain-English message (e.g. PhotoTooLargeError: "too large to
        // upload … has NOT been saved"). Longer duration so it can be read.
        const message = err?.message || 'Photo capture failed.';
        toast.error(message, { duration: 8000 });
      }
    },
    [
      disabled,
      vesselId,
      recordId,
      maxCount,
      safePhotos,
      recordType,
      capturedBy,
      captureQuality,
      capture,
      emitChange,
    ],
  );

  // ── Viewer ────────────────────────────────────────────────────────────
  const openViewer = useCallback((index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  }, []);

  const closeViewer = useCallback(() => setViewerOpen(false), []);

  const handleCaptionSave = useCallback(
    (index, nextCaption) => {
      if (!caption) return;
      const target = safePhotos[index];
      if (!target || typeof target === 'string') return; // legacy strings have no caption slot
      const nextRef = nextCaption
        ? { ...target, caption: nextCaption }
        : (() => {
            const { caption: _drop, ...rest } = target;
            return rest;
          })();
      const next = safePhotos.slice();
      next[index] = nextRef;
      emitChange(next);
    },
    [caption, safePhotos, emitChange],
  );

  // FT-350: inline caption edits from the thumbnail grid — keyed by photoId
  // (grid can't rely on indices that survive re-order). Legacy strings skipped.
  const handleCaptionChange = useCallback((photoId, nextCaption) => {
    if (!caption || !photoId) return;
    emitChange(safePhotos.map((p) => {
      if (typeof p === 'string' || p?.photoId !== photoId) return p;
      if (nextCaption) return { ...p, caption: nextCaption };
      const { caption: _drop, ...rest } = p;
      return rest;
    }));
  }, [caption, safePhotos, emitChange]);

  // ── Delete ────────────────────────────────────────────────────────────
  const requestDelete = useCallback((index) => {
    setPendingDeleteIndex(index);
  }, []);

  const cancelDelete = useCallback(() => setPendingDeleteIndex(null), []);

  const confirmDelete = useCallback(async () => {
    const idx = pendingDeleteIndex;
    if (idx == null) return;
    const target = safePhotos[idx];
    // Only PhotoRefs live in IDB — legacy base64 strings sit inline on the
    // record, so we just splice them from the array.
    if (target && typeof target !== 'string' && target.photoId) {
      try {
        await store.deletePhoto(target.photoId);
      } catch (err) {
        toast.error(err?.message || 'Failed to delete photo.');
        setPendingDeleteIndex(null);
        return;
      }
    }
    const next = safePhotos.filter((_, i) => i !== idx);
    // FT-565: in tombstone mode the deleted PhotoRef is KEPT (marked
    // `deleted_at`) so a sync merge can't resurrect it. Legacy strings
    // can't carry a marker → spliced as before.
    emitChange(next, tombstonesFor(target, tombstoneOnDelete, capturedBy));
    setPendingDeleteIndex(null);
    // If the viewer was pointing at the deleted photo, adjust or close.
    if (viewerOpen) {
      if (next.length === 0) setViewerOpen(false);
      else setViewerIndex((prev) => Math.min(prev, next.length - 1));
    }
  }, [pendingDeleteIndex, safePhotos, store, emitChange, viewerOpen, tombstoneOnDelete, capturedBy]);

  // ── Replace (FT-349d) ─────────────────────────────────────────────────
  // Delete the current photo + immediately re-trigger camera capture. Legacy
  // string entries have no photoId — Replace button is hidden for them.
  const handleReplace = useCallback(async (index) => {
    const target = safePhotos[index];
    if (!target || typeof target === 'string' || !target.photoId) return;
    try {
      await store.deletePhoto(target.photoId);
    } catch (err) {
      // Log but continue — the Replace user intent is "swap this photo now";
      // best-effort delete keeps the flow moving even if the store call fails.
      devWarn('[FT-349d] handleReplace: deletePhoto failed', {
        photoId: target.photoId,
        error: err?.message,
      });
    }
    const next = safePhotos.filter((_, i) => i !== index);
    // FT-565: tombstone the replaced ref in tombstone mode (see confirmDelete).
    emitChange(next, tombstonesFor(target, tombstoneOnDelete, capturedBy));
    setViewerOpen(false);
    // rAF gap lets the viewer unmount before we click the hidden file input —
    // otherwise iOS Safari can swallow the click when the modal is still up.
    requestAnimationFrame(() => {
      captureButtonRef.current?.triggerCapture?.();
    });
  }, [safePhotos, store, emitChange, tombstoneOnDelete, capturedBy]);

  const showCaptureButton = !disabled;
  const capReached = safePhotos.length >= maxCount;

  return (
    <div className={`space-y-3 ${className}`} data-testid={testId}>
      {showCaptureButton && (
        <PhotoCaptureButton
          ref={captureButtonRef}
          onFilesPicked={handleFilesPicked}
          isProcessing={capture.isProcessing}
          disabled={disabled}
          count={safePhotos.length}
          maxCount={maxCount}
          testId={`${testId}-button`}
        />
      )}

      {safePhotos.length === 0 ? (
        <p
          className="text-sm text-gray-500"
          data-testid={`${testId}-empty`}
        >
          {emptyLabel}
        </p>
      ) : (
        <PhotoThumbnailGrid
          photos={safePhotos}
          store={store}
          onOpen={openViewer}
          onDelete={requestDelete}
          onCaptionChange={handleCaptionChange}
          caption={caption}
          disabled={disabled}
          testId={`${testId}-grid`}
        />
      )}

      {capReached && !disabled && (
        <p
          className="text-xs text-gray-500"
          data-testid={`${testId}-cap-info`}
        >
          Maximum of {maxCount} photos per record.
        </p>
      )}

      <PhotoViewer
        isOpen={viewerOpen}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={closeViewer}
        photos={safePhotos}
        store={store}
        caption={caption}
        onCaptionSave={handleCaptionSave}
        onRequestDelete={requestDelete}
        onRequestReplace={handleReplace}
        disabled={disabled}
      />

      <PhotoDeleteConfirm
        isOpen={pendingDeleteIndex != null}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      <PhotoProcessingOverlay
        visible={capture.isProcessing}
        progress={capture.progress}
        onCancel={capture.cancel}
      />
    </div>
  );
}

export default PhotoAttachment;
